import { FilAriane } from "./FilAriane.tsx";
import type { CSSProperties, ReactNode } from "react";
import type { Role } from "@hopitech/contracts";
import { useRoute } from "../lib/router.tsx";
import { EDITIONS, ROLES_UI, accueilRole, metaPour, navPour } from "./config.ts";
import { useApp } from "./state.tsx";

/** La coquille : bannière de bascule, sidebar (édition, nav, rôle), header, contenu. */
export function Shell({ children }: { children: ReactNode }): React.ReactElement {
  const { chemin, naviguer } = useRoute();
  const app = useApp();
  const meta = metaPour(chemin);
  const groupes = navPour(app.role);
  const initiales = (app.compte || "HT").slice(0, 2).toUpperCase();

  const varsTheme = { "--accent": app.edition.theme.accent, "--soft": app.edition.theme.soft } as CSSProperties;

  return (
    <div style={{ ...varsTheme, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {app.usurpation.actif && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "10px 22px", background: "#3B2A12", color: "#FFE9C7", fontSize: 13 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="pill" style={{ background: "#E3A64A", color: "#3B2A12" }}>Bascule</span>
            <span>Vous agissez en tant que <strong style={{ color: "#fff" }}>{app.usurpation.cible}</strong>. Les actes engageants sont désactivés.</span>
          </div>
          <button className="btn btn-ghost" style={{ borderColor: "#7A6236", color: "#FFE9C7" }} onClick={app.arreterUsurpation}>Revenir à mon compte</button>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0, alignItems: "stretch" }}>
        <aside style={{ width: 270, flex: "0 0 270px", background: "var(--surface)", borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "20px 18px 14px" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }} className="display">H</div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className="display" style={{ fontSize: 17, color: "var(--accent)", lineHeight: 1.1 }}>Hopitech</span>
              <span className="eyebrow">Plateforme salon</span>
            </div>
          </div>

          <div style={{ padding: "0 14px 14px" }}>
            <div className="card" style={{ overflow: "hidden" }}>
              <div style={{ height: 4, background: "var(--accent)" }} />
              <div style={{ padding: "10px 12px 11px" }}>
                <span className="eyebrow" style={{ display: "block", marginBottom: 5 }}>Édition courante</span>
                <select
                  className="display"
                  value={app.edition.id}
                  onChange={(e) => void app.changerEdition(Number(e.target.value))}
                  style={{ width: "100%", border: 0, background: "transparent", fontSize: 15, cursor: "pointer", padding: 0, margin: "0 0 2px" }}
                >
                  {EDITIONS.map((e) => <option key={e.id} value={e.id}>{e.optionLabel}</option>)}
                </select>
                <span style={{ display: "block", fontSize: 12, color: "var(--muted)" }}>{app.edition.ville} · {app.edition.dates}</span>
              </div>
            </div>
          </div>

          <nav style={{ flex: 1, overflowY: "auto", padding: "2px 10px 14px", display: "flex", flexDirection: "column", gap: 16 }}>
            {groupes.map((g) => (
              <div key={g.label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span className="eyebrow" style={{ padding: "0 10px 6px", color: "var(--faint)" }}>{g.label}</span>
                {g.items.map((it) => {
                  const actif = chemin === it.route;
                  return (
                    <button
                      key={it.route}
                      onClick={() => naviguer(it.route)}
                      style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", border: 0, borderRadius: 9, padding: "8px 10px", cursor: "pointer", fontWeight: actif ? 700 : 500, background: actif ? "var(--soft)" : "transparent", color: actif ? "var(--accent)" : "var(--ink-2)" }}
                    >
                      <span style={{ width: 5, height: 5, borderRadius: 999, flex: "0 0 5px", background: actif ? "var(--accent)" : "var(--line-2)" }} />
                      <span style={{ flex: 1 }}>{it.label}</span>
                      <span className="mono" style={{ fontSize: 10, color: actif ? "var(--accent)" : "var(--faint)" }}>{it.ref}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <div style={{ borderTop: "1px solid var(--line)", padding: "12px 14px 16px", display: "flex", flexDirection: "column", gap: 8, background: "var(--surface-2)" }}>
            <span className="eyebrow" style={{ color: "var(--faint)" }}>Je consulte en tant que</span>
            <select
              className="select"
              value={app.role}
              onChange={(e) => {
                const r = e.target.value as Role;
                void app.changerRole(r).then(() => naviguer(accueilRole(r)));
              }}
              style={{ fontWeight: 600 }}
            >
              {ROLES_UI.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            {app.role === "administrateur" && !app.usurpation.actif && (
              <button className="btn btn-ghost" style={{ borderStyle: "dashed", justifyContent: "flex-start" }}
                onClick={() => (app.mode === "better-auth" ? naviguer("/admin/comptes") : app.demarrerUsurpation("DELABIE — Clémence Turpin"))}>↳ Basculer sur un compte…</button>
            )}
          </div>
        </aside>

        <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <header style={{ borderBottom: "1px solid var(--line)", background: "#FFFFFFEE", backdropFilter: "blur(6px)", position: "sticky", top: 0, zIndex: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "14px 26px" }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span className="pill pill-accent">{meta.ref}</span>
                  <h1 className="display" style={{ margin: 0, fontSize: 20 }}>{meta.label}</h1>
                </div>
                <span style={{ fontSize: 12.5, color: "var(--muted-2)" }}>{meta.sub}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 9, borderLeft: "1px solid var(--line)", paddingLeft: 12 }}>
                <div style={{ width: 31, height: 31, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, background: "var(--soft)", color: "var(--accent)" }}>{initiales}</div>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{app.compte || "Compte démo"}</span>
                  <span style={{ fontSize: 11, color: "var(--muted-2)" }}>{ROLES_UI.find((r) => r.id === app.role)?.label}</span>
                </div>
                <button className="btn btn-ghost" style={{ marginLeft: 6 }} onClick={() => { app.deconnecter(); naviguer("/login"); }}>Quitter</button>
              </div>
            </div>
          </header>

          <div className="hopi-in" style={{ flex: 1, padding: "26px 26px 96px" }}><FilAriane />{children}</div>
        </main>
      </div>
    </div>
  );
}
